import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Syringe, RefreshCw, Info, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from "@/components/ui/badge";

const VaccineSchedulesPage = () => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vaccine-schedules`, { withCredentials: true });
      setSchedules(res.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      toast.error('Error al cargar los esquemas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDays = (dias) => {
    if (dias >= 365) {
      const years = Math.floor(dias / 365);
      return `${years} año${years > 1 ? 's' : ''}`;
    }
    if (dias >= 30) {
      const months = Math.floor(dias / 30);
      return `${months} mes${months > 1 ? 'es' : ''}`;
    }
    return `${dias} días`;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="vaccine-schedules-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Esquemas de Vacunación</h1>
            <p className="text-slate-500 mt-1">Vista de consulta de los intervalos entre dosis por vacuna</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800 flex-1">
                <p className="font-medium mb-1">Esta es una vista de solo lectura</p>
                <p className="mb-3">
                  Los esquemas se configuran ahora directamente en <strong>Catálogos → Vacunas</strong>, 
                  editando el esquema de dosis de cada vacuna (dosis + intervalos en un solo paso). 
                  Estos intervalos generan las alertas automáticas de próxima dosis.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2 bg-white"
                  onClick={() => navigate('/catalogs')}
                  data-testid="go-to-catalogs-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ir a Catálogos
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedules (read-only) */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="loading-spinner w-8 h-8"></div>
          </div>
        ) : schedules.length === 0 ? (
          <Card className="p-8 text-center">
            <Syringe className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-4">No hay esquemas configurados</p>
            <Button variant="outline" onClick={() => navigate('/catalogs')}>
              Configurar en Catálogos
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {schedules.map((schedule) => (
              <Card 
                key={schedule.vacuna}
                className="hover:shadow-md transition-shadow"
                data-testid={`schedule-card-${schedule.vacuna}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Syringe className="w-4 h-4 text-blue-500" />
                        {schedule.vacuna}
                      </h3>
                      {schedule.descripcion && (
                        <p className="text-sm text-slate-500 mt-1">{schedule.descripcion}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="font-mono">
                      {schedule.total_dosis} dosis
                    </Badge>
                  </div>

                  {schedule.intervalos.length > 0 ? (
                    <div className="space-y-2">
                      {schedule.intervalos.map((intervalo) => (
                        <div 
                          key={`${intervalo.de_dosis}-${intervalo.a_dosis}`}
                          className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded"
                        >
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{intervalo.de_dosis}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-medium">{intervalo.a_dosis}</span>
                          <span className="text-slate-400">:</span>
                          <Badge variant="secondary" className="font-mono">
                            {formatDays(intervalo.dias)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 py-2 text-center bg-slate-50 rounded">
                      Sin intervalos configurados (no genera alertas)
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VaccineSchedulesPage;
